/*
 * pes-shared-quote.js — token-gated shared quote view (P1).
 *
 * Powers /pages/shared-quote?token=…. The link is forwarded by the contractor
 * to their client — NO sign-in, NO email wall; the unguessable token is the
 * capability. Fetches the masked payload from GET {ep}/shared/{token} where
 * the backend has already applied the chosen price-visibility mode:
 *   full       — quoted prices, per-line savings, totals
 *   price_only — retail unit prices and totals, no discount fields
 *   none       — names, SKUs, quantities only (no numeric price anywhere)
 *
 * Customer-facing copy never mentions suppliers/dropship; quotes never
 * promise stock (footer on every render).
 */

(function () {
  'use strict';

  function money(n) {
    var v = Number(n);
    if (!isFinite(v)) return '$0.00';
    return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function expiryLabel(days, expired) {
    if (expired) return 'Expired';
    if (days === null || days === undefined) return '';
    if (days <= 0) return 'Expires today';
    if (days === 1) return 'Expires in 1 day';
    return 'Expires in ' + days + ' days';
  }

  class PesSharedQuote extends HTMLElement {
    connectedCallback() {
      this.endpoint = this.getAttribute('data-endpoint') || '/apps/quotes';
      this.root = this;
      this.innerHTML = '<div class="pesqp-wrap"><p class="pesqp-loading">Loading quote&hellip;</p></div>';
      var token = new URLSearchParams(window.location.search).get('token');
      if (!token) return this.renderError('This share link is missing its token.');
      this.token = token;
      var self = this;
      fetch(this.endpoint + '/shared/' + encodeURIComponent(token))
        .then(function (r) {
          return r.json().then(function (j) {
            if (!r.ok) throw new Error(j.error || 'Quote unavailable');
            self.paint(j);
          });
        })
        .catch(function (e) { self.renderError(e.message); });
    }

    paint(q) {
      var showPrices = q.mode === 'full' || q.mode === 'price_only';
      var showSavings = q.mode === 'full';

      var rows = (q.lines || []).map(function (l) {
        var priceCells = '';
        if (showPrices) {
          var savings = showSavings && l.savings_pct != null
            ? '<span class="pesqp-save-badge">Save ' + l.savings_pct + '%</span>' : '';
          var list = showSavings && l.list_price != null && l.unit_price < l.list_price
            ? '<span class="pesqp-line__list">' + money(l.list_price) + '</span>' : '';
          priceCells = '<td>' + money(l.unit_price) + list + savings + '</td><td>' + money(l.line_total) + '</td>';
        }
        return (
          '<tr>' +
            '<td><span class="pesqp-line__title">' + esc(l.title) + '</span>' +
              (l.sku ? '<span class="pesqp-line__sku">SKU ' + esc(l.sku) + '</span>' : '') + '</td>' +
            '<td>' + esc(l.qty) + '</td>' +
            priceCells +
          '</tr>'
        );
      }).join('');

      var headCells = showPrices ? '<th>Unit price</th><th>Line total</th>' : '';
      var totalRow = showPrices
        ? '<div class="pesqp-totalrow"><span>Estimated total</span><span>' + money(q.total) + '</span></div>' +
          (showSavings && q.total_list && q.total_list > q.total
            ? '<p class="pesqp-muted" style="text-align:right;margin-top:-8px">List value ' + money(q.total_list) + '</p>'
            : '')
        : '';
      var note = q.note ? '<div class="pesqp-shared__note">' + esc(q.note) + '</div>' : '';
      // PDF download mirrors the link's own masking mode server-side: a
      // "none"-mode share yields a PDF with no prices or totals anywhere.
      var pdfBtn = '<p style="margin:14px 0 0"><a class="button button--secondary" href="' + this.endpoint +
        '/shared/' + encodeURIComponent(this.token) + '/pdf">Download PDF</a></p>';
      var previewBanner = q.preview
        ? '<div class="pesqp-banner pesqp-banner--warn pesqp-shared__preview">Preview &mdash; this is exactly what your client will see with this link. ' +
            'This preview expires ' + esc(q.preview_expires_at ? new Date(q.preview_expires_at).toLocaleString() : 'in 15 minutes') +
            ' and is not a share link.</div>'
        : '';

      this.innerHTML =
        '<div class="pesqp-wrap">' +
          previewBanner +
          '<div class="pesqp-head">' +
            '<h1 class="pesqp-title">Quote: ' + esc(q.name) + '</h1>' +
            '<span class="pesqp-muted">' + esc(q.quote_no) + ' &middot; ' +
              esc(expiryLabel(q.expires_in_days, q.expired)) + '</span>' +
          '</div>' +
          note +
          '<table class="pesqp-table"><thead><tr><th>Item</th><th>Qty</th>' + headCells + '</tr></thead>' +
            '<tbody>' + (rows || '<tr><td colspan="4" class="pesqp-muted">No items on this quote.</td></tr>') + '</tbody></table>' +
          totalRow +
          pdfBtn +
          '<p class="pesqp-footer">' + esc(q.footer ||
            'Prices held until the expiry date on eligible items. Availability confirmed at order time.') + '</p>' +
        '</div>';
    }

    renderError(msg) {
      var isExpiredPreview = /preview link expired/i.test(msg || '');
      this.innerHTML =
        '<div class="pesqp-wrap">' +
          '<h1 class="pesqp-title">' + (isExpiredPreview ? 'Preview expired' : 'Quote unavailable') + '</h1>' +
          '<p class="pesqp-muted">' + (isExpiredPreview
            ? 'Preview links last 15 minutes. Go back to the quote and choose &ldquo;Preview as Client&rdquo; again for a fresh one.'
            : 'This shared quote link is invalid or has been revoked. Please ask the sender for a new link.') + '</p>' +
          '<p class="pesqp-error">' + esc(msg) + '</p>' +
        '</div>';
    }
  }

  if (!customElements.get('pes-shared-quote')) {
    customElements.define('pes-shared-quote', PesSharedQuote);
  }
})();
